// Screen-space projection shared by the `?debug=interaction` hooks and menu
// picking (plan 17 — the third consumer earned the extraction). Pure math
// over the same camera `frameCube` gives the renderer, so screen positions
// always agree with what the last frame drew.

import { frameCube } from "../camera";
import type { Vec3 } from "../game/surface";
import type { Mat4 } from "../math";

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

/** The canvas placement in client space — `getBoundingClientRect`'s shape. */
export interface ViewRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Cube-local point to client-space pixels, or null if it is at or behind the
 * camera plane. `model` is the model matrix the frame drew with; the view is
 * a pure -z translation, then the perspective divide.
 */
export function toScreen(point: Vec3, model: Mat4, rect: ViewRect): ScreenPoint | null {
  const cam = frameCube(rect.width / rect.height);
  const m = model;
  const x = m[0] * point[0] + m[4] * point[1] + m[8] * point[2] + m[12];
  const y = m[1] * point[0] + m[5] * point[1] + m[9] * point[2] + m[13];
  const z = m[2] * point[0] + m[6] * point[1] + m[10] * point[2] + m[14] - cam.dist;
  if (z >= -1e-6) return null;
  const f = 1 / Math.tan(cam.fovY / 2);
  return {
    x: rect.left + ((((f / cam.aspect) * x) / -z + 1) / 2) * rect.width,
    y: rect.top + ((1 - (f * y) / -z) / 2) * rect.height,
  };
}
